import type { GroupNode, ConditionNode } from '../types';

type ParseFunction = (raw: Record<string, unknown>) => ConditionNode;

function tryParseGroup(
  source: Record<string, unknown>,
  key: 'and' | 'or',
  parseChild: ParseFunction,
): GroupNode | null {
  const value = source[key];
  if (value == null) return null;

  let items: unknown[];
  if (Array.isArray(value)) {
    items = value;
  } else if (typeof value === 'object' && 'children' in (value as Record<string, unknown>)) {
    const children = (value as Record<string, unknown>).children;
    if (!Array.isArray(children)) return null;
    items = children;
  } else {
    return null;
  }

  const children = items
    .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object' && !Array.isArray(item))
    .map(parseChild);

  return { type: 'group', operator: key, children };
}

/**
 * Parse group conditions: { and: [...] }, { or: [...] }, { and: { children: [...] } }, { not: {...} }
 * Returns null if the object is not a group.
 */
export function parseGroup(raw: Record<string, unknown>, parseChild: ParseFunction): GroupNode | null {
  // Handle { not: { and: [...] } } or { not: { or: [...] } } — unwrap the negation
  // Individual conditions inside carry their own inverse flags
  if ('not' in raw && typeof raw.not === 'object' && raw.not != null && !Array.isArray(raw.not)) {
    const inner = raw.not as Record<string, unknown>;
    const innerAnd = tryParseGroup(inner, 'and', parseChild);
    if (innerAnd != null && innerAnd.children.length > 0) return innerAnd;
    const innerOr = tryParseGroup(inner, 'or', parseChild);
    if (innerOr != null && innerOr.children.length > 0) return innerOr;
    // Single negated condition — parse it directly
    const singleChild = parseChild(inner);
    return { type: 'group', operator: 'and', children: [singleChild] };
  }

  // Prefer whichever key has actual content
  const andResult = tryParseGroup(raw, 'and', parseChild);
  const orResult = tryParseGroup(raw, 'or', parseChild);

  if (andResult != null && andResult.children.length > 0) return andResult;
  if (orResult != null && orResult.children.length > 0) return orResult;
  return andResult ?? orResult;
}
